import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import postApi from 'utils/api/post/PostApi';
import { Redirect } from 'react-router';
import { Button, Icon, message } from 'antd';
import PublishModal from './PublishModal';
import ScheduleForm from './ScheduleForm/ScheduleForm';
import './PublishForm.less';
import 'pages/MainLayout/ContentLayout/styles/FormCommon.less';

const { localStorage } = window;

const PublishForm = (props) => {
  const { data, offerType, postToEdit } = props;

  // Use state to know if the data is ready.
  const [dataReady, setDataReady] = useState(false);
  // Use state to know if the post was already published.
  const [mongoID, setMongoID] = useState('');
  const [redirect, setRedirect] = useState(false);
  const [scheduleData, setScheduleData] = useState({ error: false });

  const token = localStorage.getItem('token');
  // eslint-disable-next-line no-unused-vars
  const [picture, authorName, authorId] = localStorage.getItem('user').split(',');

  /**
   * useEffect on forms mutate.
   */
  useEffect(() => {
    // If data is empty then the data isn't ready.
    if (Object.entries(data).length === 0 && data.constructor === Object) {
      setDataReady(false);
      // Reset the mongo ID if the user click again on Sider Menu after edit an post.
      setMongoID('');
    } else {
      // eslint-disable-next-line no-underscore-dangle
      if (postToEdit.data && postToEdit.data._id) {
        // eslint-disable-next-line no-underscore-dangle
        setMongoID(postToEdit.data._id);
      }
      setDataReady(true);
    }
  }, [data]);

  /**
   * Determinates the keyword to generate the API path.
   */
  const getType = {
    'Oferta General': { label: 'general', int: 1 },
    'Vuelo Sencillo': { label: 'simple', int: 2 },
    'Vuelo Redondo': { label: 'round', int: 3 },
    'Vuelo más Hotel': { label: 'hotel', int: 4 },
    'Oferta de Hotel': { label: 'hotel_post', int: 5 },
  };

  const type = getType[offerType];

  const handleRedirect = () => {
    setRedirect(true);
  };

  const fillPushNotificationData = (postID) => {
    if (data.push_notification) {
      data.push_notification.id = postID;
      if (type.int === 1) {
        data.push_notification.lowest_price = 0;
      } else {
        data.push_notification.lowest_price = data.lowest_price;
      }
    }
  };

  /**
   * knownErrorHandler
   * @param {String} text Message to be analized
   * @returns {boolean} text matched with a specific case and proper message was displayed
   */
  const knownErrorHandler = (text) => {
    const knownErrorMessages = {
      provider_permalink: 'Uno o más de los permalinks se encuentra vacio',
    };
    let errorFound = false;

    Object.keys(knownErrorMessages).forEach((key) => {
      const keyRegex = new RegExp(key);
      if (keyRegex.test(text)) {
        message.warning(knownErrorMessages[key], 5);
        errorFound = true;
      }
    });

    return errorFound;
  };

  /**
   * updatePostHandler
   * @returns {object}
   */
  const updatePostHandler = async (publish = false) => {
    try {
      data.author = {
        id: authorId,
        name: authorName,
      };
      data.type = type.int;

      if (publish) data.status = 'publish';
      else data.status = 'draft';
      const response = await postApi.updatePost(type.label, data, mongoID, token);
      // If it was updated correctly.
      if (response.status === 200) {
        const successMessage = {
          true: 'Post actualizado como publicado',
          false: 'Post actualizado como borrador',
        };
        message.success(successMessage[publish]);
        if (publish) handleRedirect();
      } else {
        message.error(response.status);
      }
    } catch (error) {
      if (!knownErrorHandler(error.response.data.error[0].param)) {
        message.error(error.toString());
      }
    }
  };

  /**
   * uploadPostHandler
   * @returns {object}
   */
  const uploadPostHandler = async () => {
    try {
      data.author = {
        id: authorId,
        name: authorName,
      };
      data.type = type.int;
      const response = await postApi.uploadPost(type.label, data, token);
      // If it was uploaded correctly.
      if (response.status === 201) {
        // eslint-disable-next-line no-underscore-dangle
        setMongoID(response.data._id);
        // eslint-disable-next-line no-underscore-dangle
        fillPushNotificationData(response.data._id);
        message.success('Post guardado como borrador');
      } else {
        message.error(response.status);
      }
    } catch (error) {
      console.error(error);
      if (!knownErrorHandler(error.response.data.error[0].param)) {
        message.error(error.toString());
      }
    }
  };

  /**
   * Determine which function invokes
   */
  const clickHandler = () => {
    // If it's the first upload
    if (mongoID === '') {
      uploadPostHandler();
    } else {
      updatePostHandler();
    }
  };

  /**
   * Update the schedule data.
   */
  const handleScheduleData = (scheduleDataTemp) => {
    setScheduleData(scheduleDataTemp);
  };

  /**
   * useEffect on schedule form mutate.
   */
  useEffect(() => {
    if (!scheduleData.error && scheduleData.publish_date) {
      data.publish_date = scheduleData.publish_date;
    }
  }, [scheduleData]);

  if (redirect) {
    return <Redirect to="/" />;
  }

  return (
    <div className="publish-container">
      <div>
        <h2 className="form-title">Publicar</h2>
      </div>
      <div className="publish-main-form">
        <div className="publish-form">
          <PublishModal
            handleRedirect={handleRedirect}
            mongoID={scheduleData.error ? '' : mongoID}
            offerType={offerType}
            postToEdit={postToEdit.render}
            updatePostHandler={updatePostHandler}
            data={data}
          />
          <div className="border-div" />
          <h4>Programar publicación</h4>
          <ScheduleForm
            disabled={!mongoID}
            handleRequest={handleScheduleData}
            // eslint-disable-next-line camelcase
            publishDate={postToEdit.data && postToEdit.data.publish_date ? postToEdit.data.publish_date : null}
            updatePostHandler={updatePostHandler}
            postToEdit={postToEdit.render}
          />
        </div>
        <div className="border-div" />
        <div className="expire-form">
          <Button className="publish-expire-button">{`Expirar "${offerType}"`}</Button>
        </div>
        <div className="border-div" />
        <div className="delete-form">
          <Button type="link" className="delete-button">
            <Icon type="delete" className="delete-icon" />
            Enviar publicación a la papelera
          </Button>
        </div>
        <div className="draft-form">
          <Button
            className={`publish-draft-button ${dataReady && 'publish-blue-button'} `}
            type="primary"
            onClick={() => clickHandler()}
            disabled={!dataReady}
            ghost
          >
            {postToEdit.render ? 'Actualizar y Guardar como Borrador' : 'Guardar Como Borrador'}
          </Button>
        </div>
      </div>
    </div>
  );
};

PublishForm.defaultProps = {
  postToEdit: {
    data: {},
    render: false,
  },
};

PublishForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
  offerType: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  postToEdit: PropTypes.object,
};

export default PublishForm;
